"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const ramda_1 = require("ramda");
const client_library_1 = require("@seneca/client-library");
const small_utils_library_1 = require("@seneca/small-utils-library");
const AssignmentBadRequestError_1 = __importDefault(require("./errors/AssignmentBadRequestError"));
const AssignmentBodyNotValidError_1 = __importDefault(require("./errors/AssignmentBodyNotValidError"));
const AssignmentDoesNotExistError_1 = __importDefault(require("./errors/AssignmentDoesNotExistError"));
const BadRequestParametersError_1 = __importDefault(require("./errors/BadRequestParametersError"));
const EntityNotFoundError_1 = __importDefault(require("./errors/EntityNotFoundError"));
const MarkExamQuestionNotFoundError_1 = __importDefault(require("./errors/MarkExamQuestionNotFoundError"));
const SchoolNotFoundError_1 = __importDefault(require("./errors/SchoolNotFoundError"));
const UserNotPermittedError_1 = __importDefault(require("./errors/UserNotPermittedError"));
class AssignmentsServiceClient extends client_library_1.Client {
    constructor(fetch, options) {
        super(fetch, options);
    }
    async createAssignment(assignment) {
        const { classId, ...restOfAssignment } = assignment;
        return await this._makeClassAssignmentRequest({
            method: 'POST',
            classId,
            body: restOfAssignment,
            noRetries: true
        });
    }
    // TODO: Rename above to include Class once stable
    async createAssignmentV2(assignment, schoolId) {
        const url = `${this.url}/api/schools/${schoolId}/assignments`;
        const expectedResponses = [200, 201, 204, 400, 403, 404, 422];
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify(assignment)
        }, expectedResponses, 
        // Refer to comment in _makeClassAssignmentRequest for reasoning behind this
        Infinity);
        if (response.status === 400) {
            const body = response.json;
            throw new AssignmentBadRequestError_1.default(body.reason);
        }
        if (response.status === 422) {
            const body = response.json;
            throw new AssignmentBodyNotValidError_1.default(body.pointer, body.reason);
        }
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        return response.json;
    }
    async fetchAssignment(assignmentId) {
        return await this._makeAssignmentRequest({
            method: 'GET',
            assignmentId
        });
    }
    async fetchClassAssignment(classId, assignmentId) {
        return await this._makeClassAssignmentRequest({
            method: 'GET',
            classId,
            assignmentId
        });
    }
    async fetchTeacherAssignmentsForClass(classId, date, limit = 100, archived) {
        return this._pageMethod({
            method: this.fetchPageOfTeacherAssignmentsForClass.bind(this),
            options: {
                limit,
                date,
                classId,
                archived
            }
        });
    }
    async fetchTeacherAssignments({ from, to, limit = 100 }) {
        return this._pageMethod({
            method: this.fetchPageOfTeacherAssignments.bind(this),
            options: {
                from,
                to,
                limit
            }
        });
    }
    async fetchPageOfTeacherAssignments(options) {
        const { schoolId, from, to, limit, lastKey } = options;
        const url = `${this.url}/api/teacher/me/assignments/top-level?${this.generateQueryParamString({
            ...(schoolId && { schoolId }),
            ...(from && { from }),
            ...(to && { to }),
            ...(limit && { limit }),
            ...(lastKey && { lastKey })
        })}`;
        const expectedResponses = [200, 400, 403];
        const response = await this.requestMaker.makeRequest(url, {
            method: 'GET'
        }, expectedResponses);
        if (response.status === 400) {
            throw new BadRequestParametersError_1.default();
        }
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        return response.json;
    }
    async fetchAllTeacherClassAssignments({ from, to, limit = 100, schoolId, withAssignees = false }) {
        return this._pageMethod({
            method: this.fetchPageOfTeacherClassAssignments.bind(this),
            options: {
                from,
                to,
                limit,
                schoolId,
                withAssignees
            }
        });
    }
    async fetchPageOfTeacherClassAssignments(options) {
        const { from, to, limit, lastKey, schoolId, withAssignees } = options;
        const url = `${this.url}/api/teacher/me/assignments?${this.generateQueryParamString({
            ...(from && { from }),
            ...(to && { to }),
            ...(limit && { limit }),
            ...(schoolId && { schoolId }),
            ...(withAssignees !== undefined && { withAssignees }),
            ...(lastKey && { lastKey })
        })}`;
        const expectedResponses = [200, 400, 404];
        const response = await this.requestMaker.makeRequest(url, {
            method: 'GET'
        }, expectedResponses);
        if (response.status === 404) {
            throw new SchoolNotFoundError_1.default();
        }
        if (response.status === 400) {
            throw new BadRequestParametersError_1.default();
        }
        return response.json;
    }
    async fetchPageOfTeacherAssignmentsForClass(options) {
        const { classId, lastKey, archived, date, limit } = options;
        const url = `${this.url}/api/classes/${classId}/assignments?${this.generateQueryParamString({
            ...(limit && { limit }),
            ...(typeof archived === 'boolean' && { archived }),
            ...(date && { date }),
            ...(lastKey && { lastKey })
        })}`;
        const expectedResponses = [200, 403];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        return response.json;
    }
    async fetchPageOfEnrichedAssignmentTeacher(options) {
        const { assignmentId, limit, lastKey } = options;
        const url = `${this.url}/api/teacher/me/assignments/${assignmentId}?${this.generateQueryParamString({
            ...(limit && { limit }),
            ...(lastKey && { lastKey })
        })}`;
        const expectedResponses = [200, 400, 403, 404];
        const response = await this.requestMaker.makeRequest(url, {
            method: 'GET'
        }, expectedResponses);
        if (response.status === 400) {
            throw new BadRequestParametersError_1.default();
        }
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        if (response.status === 404) {
            throw new AssignmentDoesNotExistError_1.default(assignmentId);
        }
        return response.json;
    }
    async updateAssignment(assignment) {
        const { classId, id, ...restOfAssignment } = assignment;
        return await this._makeClassAssignmentRequest({
            method: 'PATCH',
            classId,
            assignmentId: id,
            body: restOfAssignment,
            noRetries: true
        });
    }
    async updateClassAssignmentDates(dates) {
        const { classId, id, ...restOfAssignment } = dates;
        const url = `${this.url}/api/classes/${classId}/assignments/${id}/dates`;
        const expectedResponses = [200, 400, 403, 404, 422];
        const response = await this.requestMaker.makeRequest(url, {
            method: 'PATCH',
            body: JSON.stringify(restOfAssignment)
        }, expectedResponses);
        if (response.status === 400) {
            const body = response.json;
            throw new AssignmentBadRequestError_1.default(body.reason);
        }
        if (response.status === 404) {
            throw new AssignmentDoesNotExistError_1.default(id);
        }
        if (response.status === 422) {
            const body = response.json;
            throw new AssignmentBodyNotValidError_1.default(body.pointer, body.reason);
        }
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        return response.json;
    }
    async deleteAssignment(classId, assignmentId) {
        return await this._makeClassAssignmentRequest({
            method: 'DELETE',
            classId,
            assignmentId
        });
    }
    async deleteClassAssignments(assignmentId, classIds) {
        const url = `${this.url}/api/assignments/${assignmentId}/classes`;
        const expectedResponses = [204, 400, 404];
        const response = await this.requestMaker.makeRequest(url, {
            method: 'DELETE',
            body: JSON.stringify({ classIds })
        }, expectedResponses);
        if (response.status === 400) {
            const body = response.json;
            throw new AssignmentBadRequestError_1.default(body.reason);
        }
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        if (response.status === 404) {
            throw new AssignmentDoesNotExistError_1.default(assignmentId);
        }
        if (response.status === 422) {
            const body = response.json;
            throw new AssignmentBodyNotValidError_1.default(body.pointer, body.reason);
        }
    }
    async fetchStudentAssignments(classId, date, limit = 100, archived, assignmentId) {
        return this._pageMethod({
            method: this.fetchPageOfStudentAssignments.bind(this),
            options: {
                limit,
                date,
                classId,
                archived,
                assignmentId
            }
        });
    }
    async fetchPageOfStudentAssignments(options) {
        const { classId, lastKey, archived, date, limit, assignmentId } = options;
        const url = `${this.url}/api/students/me/assignments?${this.generateQueryParamString({
            ...(limit && { limit }),
            ...(date && { date }),
            ...(classId && { classId }),
            ...(typeof archived === 'boolean' && { archived }),
            ...(lastKey && { lastKey }),
            ...(assignmentId && { assignmentId })
        })}`;
        const expectedResponses = [200, 403, 404];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        if (response.status === 404) {
            throw new EntityNotFoundError_1.default(`assignment with id ${assignmentId}`);
        }
        return response.json;
    }
    async fetchChildAssignment(userId, assignmentId) {
        const url = `${this.url}/api/parent/child/${userId}/assignment/${assignmentId}`;
        const expectedResponses = [200, 400, 403, 404];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        if (response.status === 404) {
            const body = response.json;
            throw new EntityNotFoundError_1.default(body.reason);
        }
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        if (response.status === 400) {
            throw new BadRequestParametersError_1.default();
        }
        return response.json;
    }
    async fetchChildAssignments(userId, date, limit = 100, archived) {
        return this._pageMethod({
            method: this.fetchPageOfChildAssignments.bind(this),
            options: {
                userId,
                limit,
                date,
                archived
            }
        });
    }
    async fetchPageOfChildAssignments(options) {
        const { userId, lastKey, archived, date, limit } = options;
        const url = `${this.url}/api/parent/child/${userId}/assignments?${this.generateQueryParamString({
            ...(limit && { limit }),
            ...(date && { date }),
            ...(typeof archived === 'boolean' && { archived }),
            ...(lastKey && { lastKey })
        })}`;
        const expectedResponses = [200, 403];
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, expectedResponses);
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        return response.json;
    }
    async fetchExamQuestionAnswers(classId, assignmentId) {
        const url = `${this.url}/api/classes/${classId}/assignments/${assignmentId}/exam-question-answers`;
        const expectedResponses = [200, 201, 403, 404];
        const response = await this.requestMaker.makeRequest(url, {
            method: 'GET'
        }, expectedResponses);
        if (response.status === 404) {
            throw new AssignmentDoesNotExistError_1.default(assignmentId);
        }
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        return response.json;
    }
    async markAssignmentExamQuestion({ assignmentId, classId, questionId, assigneeUserId, teacherMark, embeddedAnswers, flagged }) {
        const url = `${this.url}/api/classes/${classId}/assignments/${assignmentId}/mark-exam-question`;
        const expectedResponses = [200, 201, 400, 403, 404];
        const body = {
            assigneeUserId,
            questionId,
            teacherMark,
            embeddedAnswers,
            flagged
        };
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify(body)
        }, expectedResponses);
        if (response.status === 404) {
            const body = response.json;
            throw new MarkExamQuestionNotFoundError_1.default(body.reason);
        }
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        return response.json;
    }
    async fetchClassAssignmentsDetailsInRange({ classId, from, to }) {
        const url = `${this.url}/api/class/${classId}/assignments/detail?${this.generateQueryParamString({
            from,
            to
        })}`;
        const expectedResponses = [200, 400, 403];
        const response = await this.requestMaker.makeRequest(url, {
            method: 'GET'
        }, expectedResponses);
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        if (response.status === 400) {
            throw new BadRequestParametersError_1.default();
        }
        return response.json;
    }
    async fetchEnrichedAssignmentTeacher(assignmentId, limit = 100) {
        return this._pageMethod({
            method: this.fetchPageOfEnrichedAssignmentTeacher.bind(this),
            options: {
                assignmentId,
                limit
            }
        });
    }
    async batchUpdateTeacherClassAssignments(assignmentId, updateItems) {
        const url = `${this.url}/api/teacher/me/assignments/${assignmentId}`;
        const expectedResponses = [200, 400, 403, 404];
        const chunkedUpdateItems = (0, ramda_1.splitEvery)(10, updateItems);
        return (await (0, small_utils_library_1.immutableAsyncMap)(1, chunkedUpdateItems, async (updateItemsChunk) => {
            const chunkedResponse = await this.requestMaker.makeRequest(url, {
                method: 'PATCH',
                body: JSON.stringify({ items: updateItemsChunk })
            }, expectedResponses);
            if (chunkedResponse.status === 400) {
                throw new BadRequestParametersError_1.default();
            }
            if (chunkedResponse.status === 403) {
                throw new UserNotPermittedError_1.default();
            }
            if (chunkedResponse.status === 404) {
                throw new AssignmentDoesNotExistError_1.default(assignmentId);
            }
            return chunkedResponse.json;
        })).flat();
    }
    async _makeClassAssignmentRequest({ method, classId, assignmentId, body, queryParams, noRetries }) {
        let url = `${this.url}/api/classes/${classId}/assignments`;
        if (assignmentId) {
            url += `/${assignmentId}`;
        }
        if (queryParams) {
            url += `?${this.generateQueryParamString(queryParams)}`;
        }
        const expectedResponses = [200, 201, 204, 400, 403, 404, 422];
        const response = await this.requestMaker.makeRequest(url, {
            method,
            body: body ? JSON.stringify(body) : undefined
        }, expectedResponses, 
        // requestMaker sets retries globally and doesn't have local override
        // This variable sets the initial "number of times retried" to allow us to change the number of retries
        // e.g. since retries are set to 3 globally, we can set retryCount to Infinity to ensure only a single request on failure
        noRetries ? Infinity : undefined);
        if (response.status === 400) {
            const body = response.json;
            throw new AssignmentBadRequestError_1.default(body.reason);
        }
        if (response.status === 404) {
            throw new AssignmentDoesNotExistError_1.default(assignmentId);
        }
        if (response.status === 422) {
            const body = response.json;
            throw new AssignmentBodyNotValidError_1.default(body.pointer, body.reason);
        }
        if (response.status === 403) {
            throw new UserNotPermittedError_1.default();
        }
        return response.json;
    }
    async _makeAssignmentRequest({ method, assignmentId, body, queryParams }) {
        let url = `${this.url}/api/assignments`;
        if (assignmentId) {
            url += `/${assignmentId}`;
        }
        if (queryParams) {
            url += `?${this.generateQueryParamString(queryParams)}`;
        }
        const expectedResponses = [200, 400, 404];
        const response = await this.requestMaker.makeRequest(url, {
            method,
            body: body ? JSON.stringify(body) : undefined
        }, expectedResponses);
        if (response.status === 400) {
            const body = response.json;
            throw new AssignmentBadRequestError_1.default(body.reason);
        }
        if (response.status === 404) {
            throw new AssignmentDoesNotExistError_1.default(assignmentId);
        }
        return response.json;
    }
    generateQueryParamString(queryParams) {
        if (queryParams && Object.keys(queryParams).length > 0) {
            return Object.keys(queryParams)
                .map(param => `${param}=${encodeURIComponent(queryParams[param])}`)
                .join('&');
        }
        return '';
    }
}
exports.default = AssignmentsServiceClient;
//# sourceMappingURL=AssignmentsServiceClient.js.map